/**
 * When a deep link (e.g. a technician → FOH notification) names a ticket that
 * no Dayboard date can show — no scheduled day, an estimate, deleted — the
 * advisor is never stranded: the same handoff context the Command Center
 * shows (who, why, the note) with the same canonical Accept, and the FOH
 * queue one click away. Not a second Command Center; not the FOH Workboard.
 */
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { TicketHandoffContext } from '@/components/lifecycle/TicketHandoffContext';
import type { DeepLinkFallback } from '@/hooks/useTicketDeepLinkDay';

const REASON_TEXT: Record<DeepLinkFallback['reason'], string> = {
  no_board_date: 'This ticket is not scheduled on a Dayboard day, so it cannot open on the board.',
  not_on_board: 'This ticket is not shown on the Dayboard (it is an estimate or was deleted).',
  not_found: 'This ticket could not be found or is not available to you.',
};

export function TicketDeepLinkFallback({ fallback, onClose }: { fallback: DeepLinkFallback | null; onClose: () => void }) {
  const navigate = useNavigate();
  if (!fallback) return null;
  const label = fallback.ticketNumber ? `TKT-${String(fallback.ticketNumber).padStart(4, '0')}` : 'Ticket';
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="ticket-deep-link-fallback">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>{REASON_TEXT[fallback.reason]}</DialogDescription>
        </DialogHeader>
        {fallback.reason !== 'not_found' && <TicketHandoffContext ticketId={fallback.ticketId} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={() => { onClose(); navigate('/foh-queue'); }} data-testid="ticket-deep-link-foh-queue">
            Open FOH Workboard
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
